import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';
import { AnimatePresence, motion } from 'motion/react';
import { GoCheck, GoChevronDown, GoGear } from 'react-icons/go';
import { Popover } from 'react-tiny-popover';
import { useAiMetadataApi } from '@/features/ai-agents/shared/use-ai-metadata-api';
import { SelectedAgent } from '@/features/ai-agents/shared/models';
import { getQueryParam } from '@/utils/browser';
import { getAgentDetails } from '@/features/ai-agents/shared/agent-metadata';
import { useUpdatePersonalInfo } from '@/features/my-account/useUpdatePersonalInfo';

export interface AgentSelectionDropdownProps {
  onAgentSelect?: (agent: SelectedAgent) => void;
  selectedAgent?: SelectedAgent;
}

const AI_METADATA_SESSION_KEY = `vsly_ai_metadata_resources`;

const readResourcesFromSession = (): AgentMetadata[] | null => {
  try {
    const raw = sessionStorage.getItem(AI_METADATA_SESSION_KEY);
    return raw ? (JSON.parse(raw) as AgentMetadata[]) : null;
  } catch {
    return null;
  }
};

const saveResourcesToSession = (data: AgentMetadata[]) => {
  try {
    sessionStorage.setItem(AI_METADATA_SESSION_KEY, JSON.stringify(data));
  } catch {
    // ignore
  }
};

export const AgentSelectionDropdown: React.FC<AgentSelectionDropdownProps> = ({
  onAgentSelect,
  selectedAgent,
}) => {
  const { setDefaultModel } = useUpdatePersonalInfo();
  const [isAgentPopoverOpen, setIsAgentPopoverOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [showLabelTimer, setShowLabelTimer] = useState(false);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { listAllAgentsMetadata } = useAiMetadataApi(
    getQueryParam(`local-ai`) === `1`,
  );

  const [metadataLoading, setMetadataLoading] = useState(true);
  const [resources, setResources] = useState<AgentMetadata[]>([]);

  useEffect(() => {
    setMetadataLoading(true);
    const cached = readResourcesFromSession();
    if (cached) {
      setResources(cached);
      setMetadataLoading(false);
      return;
    }
    listAllAgentsMetadata().then((data) => {
      if (data) {
        setResources(data);
        setMetadataLoading(false);
        saveResourcesToSession(data);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const triggerLabelAutoHide = useCallback(() => {
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
    }
    setShowLabelTimer(true);
    hideTimerRef.current = setTimeout(() => setShowLabelTimer(false), 3000);
  }, []);

  useEffect(() => {
    if (!selectedAgent?.id) return;
    triggerLabelAutoHide();
  }, [selectedAgent?.id, triggerLabelAutoHide]);

  useEffect(
    () => () => {
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    },
    [],
  );

  const handleAgentSelection = useCallback(
    async (type: 'agent' | 'team' | 'workflow', id: string) => {
      await setDefaultModel(getAgentDetails({ type, id }));
      if (onAgentSelect) {
        onAgentSelect({ type, id });
      }
      setIsAgentPopoverOpen(false);
    },
    [onAgentSelect],
  );

  const selectedMetadata = useMemo(
    () => getAgentDetails(selectedAgent),
    [selectedAgent],
  );

  const filteredAgents = React.useMemo(() => {
    if (
      !selectedAgent ||
      selectedAgent.type !== `agent` ||
      !resources?.length
    ) {
      return resources;
    }

    return resources?.filter(
      (agent) => selectedMetadata.businessName === agent.businessName,
    );
  }, [resources, selectedAgent, selectedMetadata]);

  return (
    <Popover
      isOpen={isAgentPopoverOpen}
      positions={[`top`, `bottom`, `left`, `right`]}
      onClickOutside={() => setIsAgentPopoverOpen(false)}
      content={
        <AgentDropdown>
          {metadataLoading && (
            <LoadingItem role="status" aria-live="polite">
              Loading...
            </LoadingItem>
          )}
          {!metadataLoading && (
            <div>
              {filteredAgents.length > 0 && (
                <>
                  {filteredAgents.map((agent) => {
                    const isSelected = agent.id === selectedAgent?.id;
                    return (
                      <DropdownItem
                        key={agent.id}
                        isSelected={isSelected}
                        onClick={() =>
                          handleAgentSelection(agent.type, agent.id || ``)
                        }
                      >
                        <CheckmarkSlot>
                          {isSelected && <GoCheck size={13} />}
                        </CheckmarkSlot>
                        <b>{agent.businessModelName}</b> - enhanced by Visually
                        with real-time data and contextual insights
                      </DropdownItem>
                    );
                  })}
                </>
              )}
            </div>
          )}
        </AgentDropdown>
      }
    >
      <AgentLabel
        onClick={() => setIsAgentPopoverOpen(!isAgentPopoverOpen)}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <GoGear size={14.5} />
        <AnimatePresence>
          {(isHovered || showLabelTimer || isAgentPopoverOpen) &&
            selectedAgent?.id && (
              <ModelNameLabel
                key="model-name"
                initial={{ opacity: 0, maxWidth: 0 }}
                animate={{ opacity: 1, maxWidth: `30rem` }}
                exit={{ opacity: 0, maxWidth: 0 }}
                transition={{ duration: 0.25, ease: `easeInOut` }}
              >
                {selectedMetadata.businessModelName}
              </ModelNameLabel>
            )}
        </AnimatePresence>
        <GoChevronDown size={14} />
      </AgentLabel>
    </Popover>
  );
};

const AgentDropdown = styled.div`
  background: white;
  border-radius: 2rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  border: solid 1px #b0b5b8;
  padding: 0.8rem 0;
  min-width: 20rem;
  max-height: 30rem;
  overflow-y: auto;
  z-index: 1000;
`;

const DropdownItem = styled.div<{ isSelected?: boolean }>`
  font-family: Inter, sans-serif;
  font-size: 1.2rem;
  padding: 1.2rem 1.6rem;
  cursor: pointer;
  transition: background 0.2s;
  display: flex;
  align-items: center;
  gap: 0.8rem;
  color: ${({ isSelected }) => (isSelected ? `#000` : `#b0b5b8`)};

  b {
    color: ${({ isSelected }) => (isSelected ? `#000` : `#b0b5b8`)};
  }

  &:hover {
    b {
      color: #537fc1;
    }

    color: #537fc1;
  }
`;

const CheckmarkSlot = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.3rem;
  flex-shrink: 0;
  color: #000;
`;

const LoadingItem = styled.div`
  font-family: Eesti, sans-serif;
  font-size: 1.4rem;
  padding: 1rem 1.6rem;
  color: #999;
`;

const AgentLabel = styled.button`
  outline: none;
  appearance: none;

  cursor: pointer;

  color: #b0b5b8;
  margin-right: 0.4rem;
  white-space: nowrap;

  display: flex;
  gap: 0.5rem;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;

  border: 1px solid #b0b5b8;
  padding: 0.5rem 1rem;
  font-size: 1.2rem;
  font-family: Inter, sans-serif;
  border-radius: 2rem;

  font-weight: 500;

  span {
    max-width: 29rem;
    text-overflow: ellipsis;
    overflow: hidden;
  }

  &&:hover {
    opacity: 0.8;
  }

  &&:active {
    opacity: 1;
  }
`;

const ModelNameLabel = styled(motion.span)`
  overflow: hidden;
  white-space: nowrap;
  font-size: 1.2rem;
  font-family: Inter, sans-serif;
  color: #b0b5b8;
  display: inline-block;
`;

export enum ModelProvider {
  VERCEL_V0 = `v0`,
  GOOGLE_GEMINI = `gemini`,
  OPENAI_GPT = `gpt`,
  ANTHROPIC_CLAUDE = `claude`,
  UNKNOWN = `unknown`,
}

export interface AgentMetadata {
  id: string;
  type: `agent` | `team` | `workflow`;

  businessName: string;
  businessId: string;
  businessModelName: string;

  modelProvider: ModelProvider;
  modelType: string;
  fullModelName: string;

  isDevModel: boolean;
}
